import { createFactory } from 'react';

// redux action creators for persistence
import * as asyncActionCreators from '../../../../redux/actionCreators/async';

// utilities
// import _ from 'lodash';

// validation object
import {getSchema as getUserSchema} from '../../../../models/user';
import {getSchema as getProfileSchema} from '../../../../models/applicantProfile';
import {getSchema as getWorkExperienceSchema} from '../../../../models/applicantWorkExperience';
import {getSchema as getEmploymentGapsSchema} from '../../../../models/employmentGap';
import {getSchema as getSkillsSummarySchema} from '../../../../models/skillsSummary';
import {getSchema as getEducationSchema} from '../../../../models/education';
import {getSchema as getLicenseSchema} from '../../../../models/license';
import {getSchema as getDocumentsSchema} from '../../../../models/documents';
import {getSchema as getDesignationasAndAssociationsSchema} from '../../../../models/designationsAndAssociations';
import {getSchema as getApplicationSubmissionSchema} from '../../../../models/applicationSubmission';
import {getSchema as getReferenceSchema} from '../../../../models/reference';

// get validation objects
const userSchema = getUserSchema(),
  profileSchema = getProfileSchema(),
  workExperienceSchema = getWorkExperienceSchema(),
  employmentGapsSchema = getEmploymentGapsSchema(),
  skillsSummarySchema = getSkillsSummarySchema(),
  educationSchema = getEducationSchema(),
  licensesSchema = getLicenseSchema(),
  documentsSchema = getDocumentsSchema(),
  designationsAndAssociationsSchema= getDesignationasAndAssociationsSchema(),
  applicationSubmissionSchema = getApplicationSubmissionSchema(),
  referenceSchema = getReferenceSchema();


// application page components
// // Profile
import ProfileInstructions from './Profile/c_Instructions';
import ContactInformation from './Profile/c_ContactInformation';
import PreliminaryInformation from './Profile/c_PreliminaryInformation';

// // Preferences
import AvailabilityInformation from './Preferences/c_AvailabilityInformation';
import HomeOfficeSetup from './Preferences/c_HomeOfficeSetup';

// // Experience
import CareerSummary from './Experience/c_CareerSummary';
import WorkExperience from './Experience/c_WorkExperience';
import EmploymentGaps from './Experience/c_EmploymentGaps';

// // Background
import LicensesContainer from '../../../applicant/LicensesForm/LicensesContainer';
import Education from './Background/c_Education';
import ProfessionalAssociations from './Background/c_ProfessionalAssociations';
import ProfessionalDesignations from './Background/c_ProfessionalDesignations';
import AdditionalReferences from './Background/c_AdditionalReferences';

// // Skill
import SkillsIntroduction from './Skills/c_Introduction';
import SummaryOfSkills from './Skills/c_SummaryOfSkills';

import validateExperienceYears, { warningExperienceYears } from './Experience/validateExperienceYears';

// Documents
import Documents from './c_Documents';

// Submit
import Submit from './c_Submit';


// retrieves current component
export function getCurrent(activeColumnNav, activeRowSubNav) {
  // look up column on 'lookup' object
  let lookupVal = lookup[activeColumnNav];

  // corresponding string
  let viewModel = activeColumnNav;

  // some components are nested, others are not, this handles that
  if(lookupVal && activeRowSubNav) {
    lookupVal = lookupVal[activeRowSubNav];
    viewModel += `.${activeRowSubNav}`;
  }
  return {...lookupVal, viewModel};
}

// object based on view nav keys used to look up components
export const lookup = {
  Profile: {
    'Contact Information': {
      component: createFactory(ContactInformation),
      model: 'user',
      validation: userSchema
    },
    'Preliminary Information': {
      component: createFactory(PreliminaryInformation),
      model: 'profile',
      validation: profileSchema
    }
  },
  Instructions: {
    component: createFactory(ProfileInstructions)
  },
  Preferences: {
    'Availability Information': {
      component: createFactory(AvailabilityInformation),
      model: 'profile',
      validation: profileSchema
    },
    'Home Office Setup': {
      component: createFactory(HomeOfficeSetup),
      model: 'profile',
      validation: profileSchema
    }
  },
  Experience: {
    'Career Summary': {
      component: createFactory(CareerSummary),
      model: 'profile',
      validation: profileSchema
    },
    'Work Experience': {
      component: createFactory(WorkExperience),
      model: 'workExperiences',
      validation: workExperienceSchema,
      submitValidator: validateExperienceYears,
      submitErrorMessage: warningExperienceYears,
      saveFunc(tempState) {
        return asyncActionCreators.persistListUpdate('workexperiences', 'workExperiences', tempState);
      }
    },
    'Employment Gaps': {
      component: createFactory(EmploymentGaps),
      model: 'employmentGaps',
      validation: employmentGapsSchema,
      saveFunc(tempState) {
        return asyncActionCreators.persistListUpdate('employmentgaps', 'employmentGaps', tempState);
      }
    }
  },
  Skills: {
    Instructions: {
      component: createFactory(SkillsIntroduction),
      title: 'Instructions'
    },
  },
  'Summary of Skills' : {
    'Summary of Skills': {
      component: createFactory(SummaryOfSkills),
      model: 'skills',
      title: 'Summary of Skills',
      validation: skillsSummarySchema,
      saveFunc(tempState) {
        return asyncActionCreators.persistSkills(tempState);
      }
    }
  },
  Background: {
    Licenses: {
      component: createFactory(LicensesContainer),
      model: ['licenses', 'profile'],
      validation: licensesSchema,
      saveFunc(tempState) {
        return asyncActionCreators.persistListUpdate('licenses', 'licenses', tempState);
      }
    },
    Education: {
      component: createFactory(Education),
      model: 'education',
      validation: educationSchema,
      saveFunc(tempState) {
        return asyncActionCreators.persistListUpdate('education', 'education', tempState);
      }
    },
    'Professional Associations': {
      component: createFactory(ProfessionalAssociations),
      model: 'associations',
      validation: designationsAndAssociationsSchema,
      validationKey: 'ProfessionalAssociations',
      customErrorMessage: 'Please Select at Least One Item.',
      saveFunc(tempState) {
        return asyncActionCreators.persistBackground(tempState, 'associations');
      }
    },
    'Professional Designations': {
      component: createFactory(ProfessionalDesignations),
      model: 'designations',
      validation: designationsAndAssociationsSchema,
      validationKey: 'ProfessionalDesignations',
      customErrorMessage: 'Please Select at Least One Item.',
      saveFunc(tempState) {
        return asyncActionCreators.persistBackground(tempState, 'designations');
      }
    },
    'Additional Professional References': {
      component: createFactory(AdditionalReferences),
      model: 'references',
      customErrorMessage: 'Please fill out the required fields above and include at least two references.',
      saveFunc(tempState) {
        return asyncActionCreators.persistListUpdate('references', 'references', tempState);
      }
    },
  },
  Documents: {
    component: createFactory(Documents),
    model: 'documents',
    validation: documentsSchema,
    saveFunc(tempState) {
      return asyncActionCreators.persistListUpdate('documents', 'documents', tempState);
    }
  },
  Submit: {
    component: createFactory(Submit),
    model: 'profile',
    validation: applicationSubmissionSchema,
    saveFunc(tempState) {
      return [asyncActionCreators.persistApplicantUpdate(tempState), asyncActionCreators.authorizeApplication(tempState)];
    }
  }
};



// WEBPACK FOOTER //
// ./shared/react/r_ApplicantDashboard/f_Application/r_ApplicationForm/componentLookup.js